import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { format } from 'date-fns';
import api from '../config/api';
import {
  StyledTableContainer,
  TableSkeleton,
  FilterBar,
  SearchInput,
  FilterSelect,
  StyledPagination,
  EmptyState,
  StatusChip,
} from '../components/TableShared';

const ff = "'Poppins', sans-serif";

const ROLE_CHIP = {
  USER: { color: '#3182CE', bgColor: '#EBF8FF' },
  SITTER: { color: '#805AD5', bgColor: '#FAF5FF' },
  ADMIN: { color: '#E53E3E', bgColor: '#FFF5F5' },
};

export default function Users() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalUsers, setTotalUsers] = useState(0);

  // Search and filter state
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('All');
  const debounceTimer = useRef(null);

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [userToEdit, setUserToEdit] = useState(null);
  const [editRole, setEditRole] = useState('');
  const [editName, setEditName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editLoading, setEditLoading] = useState(false);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const params = { page: page + 1, limit: rowsPerPage };
      if (search) params.search = search;
      if (roleFilter !== 'All') params.role = roleFilter;
      const response = await api.get('/admin/users', { params });
      const data = response.data.data || response.data;
      setUsers(data.users || []);
      setTotalUsers(data.total || data.users?.length || 0);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to load users. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, search, roleFilter]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Debounced search handler
  const handleSearchChange = useCallback((e) => {
    const value = e.target.value;
    setSearchInput(value);
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    debounceTimer.current = setTimeout(() => {
      setSearch(value);
      setPage(0);
    }, 300);
  }, []);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, []);

  const handleRoleFilterChange = (e) => {
    setRoleFilter(e.target.value);
    setPage(0);
  };

  const handleDeleteClick = (user) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!userToDelete) return;
    try {
      setDeleteLoading(true);
      await api.delete(`/admin/users/${userToDelete.id}`);
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      fetchUsers();
    } catch (err) {
      console.error('Error deleting user:', err);
      setError('Failed to delete user. Please try again.');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setUserToDelete(null);
  };

  const handleEditClick = (user) => {
    setUserToEdit(user);
    setEditRole(user.role);
    setEditName(user.fullName || '');
    setEditEmail(user.email || '');
    setEditDialogOpen(true);
  };

  const handleEditClose = () => {
    setEditDialogOpen(false);
    setUserToEdit(null);
    setEditRole('');
    setEditName('');
    setEditEmail('');
  };

  const editHasChanges = userToEdit && (
    editRole !== userToEdit.role ||
    editName !== (userToEdit.fullName || '') ||
    editEmail !== (userToEdit.email || '')
  );

  const handleEditSave = async () => {
    if (!userToEdit || !editHasChanges) return;
    try {
      setEditLoading(true);
      const body = {};
      if (editRole !== userToEdit.role) body.role = editRole;
      if (editName !== (userToEdit.fullName || '')) body.fullName = editName;
      if (editEmail !== (userToEdit.email || '')) body.email = editEmail;
      await api.put(`/admin/users/${userToEdit.id}`, body);
      setEditDialogOpen(false);
      setUserToEdit(null);
      setEditRole('');
      setEditName('');
      setEditEmail('');
      fetchUsers();
    } catch (err) {
      console.error('Error updating user:', err);
      setError('Failed to update user. Please try again.');
    } finally {
      setEditLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), 'MMM dd, yyyy');
    } catch {
      return 'N/A';
    }
  };

  return (
    <Box>
      {/* Search and Filter Bar */}
      <FilterBar>
        <SearchInput
          placeholder="Search users..."
          value={searchInput}
          onChange={handleSearchChange}
        />
        <FilterSelect
          label="Role"
          value={roleFilter}
          onChange={handleRoleFilterChange}
          options={[
            { value: 'All', label: 'All' },
            { value: 'USER', label: 'USER' },
            { value: 'SITTER', label: 'SITTER' },
            { value: 'ADMIN', label: 'ADMIN' },
          ]}
        />
      </FilterBar>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {loading ? (
        <TableSkeleton columns={7} rows={6} />
      ) : (
        <>
          <StyledTableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Active</TableCell>
                  <TableCell>Verified</TableCell>
                  <TableCell>Joined</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>{user.fullName || 'N/A'}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      <StatusChip
                        label={user.role}
                        color={ROLE_CHIP[user.role]?.color || '#718096'}
                        bgColor={ROLE_CHIP[user.role]?.bgColor || '#F7FAFC'}
                      />
                    </TableCell>
                    <TableCell>
                      <StatusChip
                        label={user.isActive ? 'Active' : 'Inactive'}
                        color={user.isActive ? '#38A169' : '#E53E3E'}
                        bgColor={user.isActive ? '#F0FFF4' : '#FFF5F5'}
                      />
                    </TableCell>
                    <TableCell>
                      <StatusChip
                        label={user.phoneVerified ? 'Verified' : 'Unverified'}
                        color={user.phoneVerified ? '#38A169' : '#D69E2E'}
                        bgColor={user.phoneVerified ? '#F0FFF4' : '#FFFFF0'}
                      />
                    </TableCell>
                    <TableCell>{formatDate(user.createdAt)}</TableCell>
                    <TableCell>
                      <IconButton size="small" color="primary" onClick={() => handleEditClick(user)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDeleteClick(user)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {users.length === 0 && (
                  <EmptyState colSpan={7} message="No users found." />
                )}
              </TableBody>
            </Table>
            <StyledPagination
              count={totalUsers}
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </StyledTableContainer>
        </>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle sx={{ fontFamily: ff }}>Delete User</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontFamily: ff }}>
            Are you sure you want to delete{' '}
            <strong>{userToDelete ? userToDelete.fullName || userToDelete.email : ''}</strong>?
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleteLoading} sx={{ fontFamily: ff }}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" disabled={deleteLoading} sx={{ fontFamily: ff }}>
            {deleteLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={handleEditClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontFamily: ff }}>Edit User</DialogTitle>
        <DialogContent>
          {userToEdit && (
            <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                fullWidth
                size="small"
                label="Full Name"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                sx={{ '& .MuiInputBase-root': { fontFamily: ff } }}
              />
              <TextField
                fullWidth
                size="small"
                label="Email"
                value={editEmail}
                onChange={(e) => setEditEmail(e.target.value)}
                sx={{ '& .MuiInputBase-root': { fontFamily: ff } }}
              />
              <FormControl fullWidth size="small">
                <InputLabel sx={{ fontFamily: ff }}>Role</InputLabel>
                <Select
                  value={editRole}
                  label="Role"
                  onChange={(e) => setEditRole(e.target.value)}
                  sx={{ fontFamily: ff }}
                >
                  <MenuItem value="USER" sx={{ fontFamily: ff }}>USER</MenuItem>
                  <MenuItem value="SITTER" sx={{ fontFamily: ff }}>SITTER</MenuItem>
                  <MenuItem value="ADMIN" sx={{ fontFamily: ff }}>ADMIN</MenuItem>
                </Select>
              </FormControl>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditClose} disabled={editLoading} sx={{ fontFamily: ff }}>Cancel</Button>
          <Button
            onClick={handleEditSave}
            variant="contained"
            color="primary"
            disabled={editLoading || !editHasChanges}
            sx={{ fontFamily: ff }}
          >
            {editLoading ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
